import type { Adapter } from '@solana/wallet-adapter-base'
import type { PublicKey } from '@solana/web3.js'
import { useWallet } from '@solana/wallet-adapter-vue'
import { Notify } from 'quasar'
import { useConnectionStore } from '~/store/connection'
import { shortenAddress } from '~/utils'
import { useEmitter } from './emitter'

export const WALLET_CONNECT_EVENT = Symbol('WALLET_CONNECT_EVENT')
export const WALLET_DISCONNECT_EVENT = Symbol('WALLET_DISCONNECT_EVENT')
export const WALLET_ERROR_EVENT = Symbol('WALLET_ERROR_EVENT')
export const ACCOUNT_CHANGE_EVENT = Symbol('ACCOUNT_CHANGE_EVENT')

const NOTIFICATION_TIMEOUT = 5000

export function initWallet() {
  const { connection } = useConnectionStore()
  const { emit } = useEmitter()
  const { wallet, connected } = useWallet()

  // Map to track cleanup functions for each registered public key
  const cleanup = new Map<string, () => void>()

  function registerAccountChange(pk: PublicKey) {
    const key = String(pk)

    if (cleanup.has(key)) {
      // Listeners already registered for this public key
      return
    }

    // Listen for account changes
    const accountChangeListenerId = connection.onAccountChange(pk, (accountInfo) => {
      console.log('ACCOUNT_CHANGE_EVENT', accountInfo)
      emit(ACCOUNT_CHANGE_EVENT, accountInfo)
    })

    // Listen for logs related to the public key
    const logsListenerId = connection.onLogs(pk, (logs) => {
      console.log('LOGS_EVENT', logs)
    })

    // Store the cleanup function for future reference
    cleanup.set(key, () => {
      connection.removeAccountChangeListener(accountChangeListenerId).then()
      connection.removeOnLogsListener(logsListenerId).then()
      cleanup.delete(key)
    })
  }

  function unregisterAccountChange() {
    for (const [,fn] of cleanup) {
      fn()
    }
    cleanup.clear()
  }

  /**
   * Handles wallet connection events.
   * Displays a notification and emits a connect event.
   * @param adapter WalletAdapter instance
   */
  const handleConnect = (adapter: Adapter) => {
    const pk = adapter.publicKey
    if (!pk) {
      return
    }
    Notify.create({
      message: 'Wallet Update',
      caption: `Connected to wallet ${shortenAddress(String(pk), 7)}`,
      timeout: NOTIFICATION_TIMEOUT,
    })
    emit(WALLET_CONNECT_EVENT, adapter)
  }

  /**
   * Handles wallet disconnection events.
   * Displays a notification and emits a disconnect event.
   * @param adapter WalletAdapter instance
   */
  const handleDisconnect = (adapter: Adapter) => {
    Notify.create({
      message: 'Wallet Update',
      caption: 'Disconnected from wallet',
      timeout: NOTIFICATION_TIMEOUT,
    })
    emit(WALLET_DISCONNECT_EVENT, adapter)
  }

  /**
   * Handles wallet error events.
   * Logs the error to the console.
   * @param error Error object
   */
  const handleError = (error: Error) => {
    if (!error.message) {
      return
    }
    console.error('Wallet Error:', error.message)
    // Notify.create({
    //   type: 'negative',
    //   message: 'Wallet Error',
    //   caption: error.message,
    //   timeout: NOTIFICATION_TIMEOUT,
    // })
    emit(WALLET_ERROR_EVENT, error)
  }

  watch(connected, useDebounceFn((connected) => {
    if (!wallet.value) {
      return
    }
    const { adapter } = wallet.value
    if (connected) {
      handleConnect(adapter)
      if (adapter.publicKey) {
        registerAccountChange(adapter.publicKey)
      }
      adapter.once('disconnect', () => {
        unregisterAccountChange()
        handleDisconnect(adapter)
      })
      adapter.removeAllListeners('error')
      adapter.on('error', handleError)
    }
  }, 500), { immediate: true })

  // watch(wallet, useDebounceFn((w) => {
  //   if (!w) {
  //     // Wallet is disconnected; no further action needed
  //     return
  //   }
  //
  //   if (publicKey.value) {
  //     registerAccountChange(publicKey.value)
  //   }
  //
  //   // w.adapter.once('connect', () => handleConnect(w.adapter))
  //   w.adapter.once('disconnect', () => {
  //     cleanupFunctions.forEach(cleanupFn => cleanupFn())
  //     cleanupFunctions.clear()
  //     handleDisconnect(w.adapter)
  //   })
  //   w.adapter.removeAllListeners('error')
  //   w.adapter.on('error', handleError)
  // }, 200), { immediate: true })
}
